import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI } from '../../utils/fetchData';
import './UsageDashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const MetricCard = ({ title, value, subtitle, color = '#00ff88' }) => (
  <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
    <div className="flex items-center gap-2 mb-2">
      <div 
        className="w-[8px] h-[8px] rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
        {title}
      </span>
    </div>
    <div className="text-white text-[18px] font-bold mb-1">{value}</div>
    {subtitle && (
      <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{subtitle}</div>
    )}
  </div>
);

const ContentCard = ({ title, children, color = '#00ff88' }) => (
  <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
    <div className="flex items-center gap-2 mb-4">
      <div 
        className="w-[8px] h-[8px] rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
        {title}
      </span>
    </div>
    {children}
  </div>
);

const UsageDashboard = () => {
  const [usageData, setUsageData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('details');

  useEffect(() => {
    loadData();
  }, [timeRange]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dizeeToken');
      
      if (!token) {
        // Provide fallback data when no token
        setUsageData({
          current_usage: 125,
          monthly_allowance: 300,
          percentage: 41.7,
          tier: 'free',
          tier_name: 'Free',
          overage: 0,
          overage_cost: 0.00,
          efficiency_multiplier: 1.5,
          team_members: [],
          category_breakdown: {
            'DIZEE AI': '45%',
            'Music Stats': '25%',
            'Catalog': '15%',
            'Social': '10%',
            'Other': '5%'
          }
        });
        
        setAnalyticsData({
          daily_data: [
            { date: '2024-01-01', total_usage: 20 },
            { date: '2024-01-02', total_usage: 25 },
            { date: '2024-01-03', total_usage: 15 },
            { date: '2024-01-04', total_usage: 35 },
            { date: '2024-01-05', total_usage: 30 },
          ],
          raw_events: [
            { action_display_name: 'DIZEE AI Request', date: '2024-01-05T10:30:00Z', total_usage: 15, request_count: 1 },
            { action_display_name: 'Music Statistics Call', date: '2024-01-05T09:15:00Z', total_usage: 100, request_count: 1 },
            { action_display_name: 'Catalog Entry', date: '2024-01-04T16:20:00Z', total_usage: 0.2, request_count: 1 },
            { action_display_name: 'Social Listening', date: '2024-01-04T14:10:00Z', total_usage: 90, request_count: 1 },
          ]
        });
        setLoading(false);
        return;
      }

      const [summaryResponse, analyticsResponse] = await Promise.all([
        getDataAPI('usage/summary', token),
        getDataAPI(`usage/analytics?period=${timeRange}`, token)
      ]);
      
      if (summaryResponse.data.success) {
        setUsageData(summaryResponse.data.data);
      }
      
      if (analyticsResponse.data.success) {
        setAnalyticsData(analyticsResponse.data.data);
      }
    } catch (error) {
      console.error('Failed to load usage data:', error);
      // Provide fallback data on error
      setUsageData({
        current_usage: 125,
        monthly_allowance: 300,
        percentage: 41.7,
        tier: 'free',
        tier_name: 'Free',
        overage: 0,
        overage_cost: 0.00,
        efficiency_multiplier: 1.5,
        team_members: [],
        category_breakdown: {
          'DIZEE AI': '45%',
          'Music Stats': '25%',
          'Catalog': '15%',
          'Social': '10%',
          'Other': '5%'
        }
      });
      
      setAnalyticsData({
        daily_data: [
          { date: '2024-01-01', total_usage: 20 },
          { date: '2024-01-02', total_usage: 25 },
          { date: '2024-01-03', total_usage: 15 },
          { date: '2024-01-04', total_usage: 35 },
          { date: '2024-01-05', total_usage: 30 },
        ],
        raw_events: [
          { action_display_name: 'DIZEE AI Request', date: '2024-01-05T10:30:00Z', total_usage: 15, request_count: 1 },
          { action_display_name: 'Music Statistics Call', date: '2024-01-05T09:15:00Z', total_usage: 100, request_count: 1 },
          { action_display_name: 'Catalog Entry', date: '2024-01-04T16:20:00Z', total_usage: 0.2, request_count: 1 },
          { action_display_name: 'Social Listening', date: '2024-01-04T14:10:00Z', total_usage: 90, request_count: 1 },
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - (timeRange === '1d' ? 1 : timeRange === '7d' ? 7 : 30));
      const endDate = new Date();
      
      const response = await fetch(`/api/usage/export?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`, {
        headers: {
          'Authorization': localStorage.getItem('dizeeToken'),
        },
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usage-data-${timeRange}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const formatNumber = (num) => {
    if (!num || isNaN(num)) return '0';
    return new Intl.NumberFormat('en-US').format(num);
  };

  if (loading) {
    return (
      <LayoutHeader>
        <div className="w-[390px] h-screen bg-black text-white flex items-center justify-center">
          <p className="text-[12px]">Loading usage analytics...</p>
        </div>
      </LayoutHeader>
    );
  }

  if (!usageData || !analyticsData) {
    return (
      <LayoutHeader>
        <div className="w-[390px] h-screen bg-black text-white flex items-center justify-center">
          <p className="text-[12px]">Failed to load usage data</p>
        </div>
      </LayoutHeader>
    );
  }

  const { 
    current_usage = 0, 
    monthly_allowance = 300, 
    percentage = 0, 
    tier = 'free',
    tier_name = 'Free',
    overage = 0, 
    overage_cost = 0,
    team_members = [],
    category_breakdown = {},
    efficiency_multiplier = 1.0
  } = usageData || {};
  
  // Prepare chart data
  const chartData = {
    labels: (analyticsData?.daily_data || []).map(d => {
      const date = new Date(d.date || Date.now());
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [{
      label: 'Daily Usage',
      data: (analyticsData?.daily_data || []).map(d => d.total_usage || 0),
      borderColor: '#00ff88',
      backgroundColor: 'rgba(0, 255, 136, 0.1)',
      tension: 0.4,
      fill: true,
      pointBackgroundColor: '#00ff88',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#00ff88',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const value = context.raw;
            const percentage = ((value / (monthly_allowance || 300)) * 100).toFixed(1);
            return `Usage: ${percentage}% of allowance`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(255,255,255,0.1)' },
        ticks: { color: 'rgba(255,255,255,0.7)' }
      },
      y: {
        grid: { color: 'rgba(255,255,255,0.1)' },
        ticks: { color: 'rgba(255,255,255,0.7)' },
        title: {
          display: true,
          text: 'Daily Usage Level',
          color: 'rgba(255,255,255,0.7)',
          font: { size: 11 }
        }
      }
    }
  };

  // Calculate activity stats
  const totalActivities = (analyticsData?.raw_events || []).reduce((sum, event) => sum + parseInt(event.request_count || 0), 0);
  const uniqueDays = new Set((analyticsData?.raw_events || []).map(event => (event.date || '').split('T')[0])).size;

  return (
    <LayoutHeader>
      <div className="w-[390px] h-screen bg-black text-white relative">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between">
            <div className="flex items-center gap-2">
              <div 
                className="w-[24px] h-[24px] rounded-md flex items-center justify-center text-[8px] font-medium text-white"
                style={{ backgroundColor: '#00ff88' }}
              >
                US
              </div>
              <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Usage Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <p 
                className='text-[red] cursor-pointer text-[12px]'
                onClick={handleExport}
              >
                Export data
              </p>
              <div 
                className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
                onClick={() => window.history.back()}
              >
                Back
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
            {/* Filter */}
            <div className="mb-6">
              <select 
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="bg-[#1A1A1A] border border-[#333] text-white text-[12px] px-3 py-2 rounded mr-4"
              >
                <option value="1d">Last 24 Hours</option>
                <option value="7d">Last Week</option>
                <option value="30d">Last Month</option>
              </select>
              
              <select 
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                className="bg-[#1A1A1A] border border-[#333] text-white text-[12px] px-3 py-2 rounded"
              >
                <option value="details">Overview</option>
                <option value="tokens">Activity</option>
              </select>
            </div>

            <div className="space-y-4">
              {/* Main Usage Progress */}
              <ContentCard title="Monthly Usage" color="#00ff88">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-[18px] font-bold">{Math.round(percentage)}%</span>
                      <span className="text-[rgba(255,255,255,0.5)] text-[12px]">used this month</span>
                    </div>
                    <div className={`text-[8px] px-2 py-1 rounded font-bold uppercase tracking-wide ${
                      tier === 'free' ? 'bg-[#666] text-white' :
                      tier === 'lite' ? 'bg-[#00ff88] text-black' :
                      tier === 'core' ? 'bg-[#0066ff] text-white' :
                      'bg-[#ff6600] text-white'
                    }`}>
                      {tier_name}
                    </div>
                  </div>
                  
                  <div className="w-full bg-[rgba(255,255,255,0.1)] rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ${
                        percentage > 90 ? 'bg-red-500' :
                        percentage > 75 ? 'bg-yellow-500' :
                        'bg-[#00ff88]'
                      }`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                  </div>
                  
                                     <div className="flex items-center justify-between text-[10px] text-[rgba(255,255,255,0.5)]">
                     <span>{(percentage || 0).toFixed(1)}% used</span>
                    <span>Resets {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
              </ContentCard>

              {/* Top Metrics Row */}
              <div className="grid grid-cols-2 gap-4">
                <MetricCard
                  title="Current Usage"
                  value={`${Math.round(percentage)}%`}
                  subtitle="Used this month"
                  color="#00ff88"
                />
                <MetricCard
                  title="Spending"
                  value={`$${(overage_cost || 0).toFixed(2)}`}
                  subtitle="Overage charges"
                  color="#ff6b35"
                />
              </div>

              {/* Second Metrics Row */}
              <div className="grid grid-cols-2 gap-4">
                <MetricCard
                  title="Efficiency"
                  value={`${efficiency_multiplier}x`}
                  subtitle={`${tier_name} multiplier`}
                  color="#1db954"
                />
                <MetricCard
                  title="Total Requests"
                  value={totalActivities}
                  subtitle="API calls made"
                  color="#9932cc"
                />
              </div>

              {/* Third Metrics Row */}
              <div className="grid grid-cols-2 gap-4">
                <MetricCard
                  title="Active Days"
                  value={uniqueDays}
                  subtitle={`Last ${timeRange}`}
                  color="#1da1f2"
                />
                <MetricCard
                  title="Daily Average"
                  value={`${Math.round(percentage / Math.max(uniqueDays, 1))}%`}
                  subtitle="Usage per day"
                  color="#ff6b35"
                />
              </div>

              {/* Chart */}
              <ContentCard title="Usage Trend" color="#00ff88">
                <div className="chart-container">
                  <Line data={chartData} options={chartOptions} />
                </div>
              </ContentCard>

              {/* Content based on view mode */}
              {viewMode === 'details' ? (
                <ContentCard title="Usage Breakdown" color="#9932cc">
                  <div className="space-y-3">
                    {Object.entries(category_breakdown || {}).map(([category, percentage]) => (
                      <div key={category} className="flex items-center justify-between text-[12px]">
                        <span className="text-[rgba(255,255,255,0.8)]">{category}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-[rgba(255,255,255,0.1)] rounded-full h-1.5">
                            <div 
                              className="h-1.5 rounded-full bg-[#00ff88] transition-all duration-500"
                              style={{ width: percentage }}
                            />
                          </div>
                          <span className="text-[#00ff88] font-semibold min-w-[40px] text-right">{percentage}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ContentCard>
              ) : (
                <ContentCard title="Recent Activity" color="#1da1f2">
                                     <div className="space-y-3">
                     {(analyticsData?.raw_events || []).slice(0, 6).map((event, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.05)] last:border-b-0">
                                                 <div className="flex flex-col">
                           <span className="text-[12px] text-white font-medium">{event.action_display_name || 'Unknown Action'}</span>
                           <span className="text-[10px] text-[rgba(255,255,255,0.5)]">
                             {new Date(event.date || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                           </span>
                         </div>
                        <div className="text-right">
                                                     <div className="text-[11px] text-[#00ff88] font-semibold">
                             {event.request_count || 1}x
                           </div>
                          <div className="text-[9px] text-[rgba(255,255,255,0.5)]">requests</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ContentCard>
              )}

              {/* Team Usage (only show if team members exist) */}
              {team_members && team_members.length > 0 && (
                <ContentCard title="Team Usage" color="#ff6b35">
                  <div className="space-y-3">
                    {team_members.map((member, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.05)] last:border-b-0">
                                                 <div className="flex flex-col">
                           <div className="flex items-center gap-2">
                             <span className="text-[12px] text-white font-medium">{member.name || 'Unknown User'}</span>
                             {member.is_current_user && <span className="bg-[#00ff88] text-black text-[8px] px-1.5 py-0.5 rounded font-bold">YOU</span>}
                           </div>
                           <span className="text-[10px] text-[rgba(255,255,255,0.5)]">{member.email || 'No email'}</span>
                         </div>
                                                 <div className="text-right">
                           <div className="text-[11px] text-[#00ff88] font-semibold">{Math.round((member.usage || 0) / (monthly_allowance || 300) * 100)}%</div>
                           <div className="text-[9px] text-[rgba(255,255,255,0.5)]">${(member.spend || 0).toFixed(2)}</div>
                         </div>
                      </div>
                    ))}
                  </div>
                </ContentCard>
              )}
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default UsageDashboard; 